import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useState } from 'react'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { useTheme } from '../../theme/ThemeContext'
import { AgentActivityBar, type AgentActivityState } from './AgentActivityBar'
import type { AgentProcessLine } from './AgentProcessFold'
import { AgentStatusDotSlot } from './AgentStatusDot'

export type AgentSubagentBlock = {
  id: string
  title: string
  status?: string
  summary?: string
  collapsedByDefault?: boolean
  forceOpen?: boolean
  lines?: AgentProcessLine[]
  activity?: AgentActivityState | null
  /** 外部持久化展开状态（页面恢复时使用） */
  externalOpen?: boolean
  /** 用户手动展开/收起回调（用于持久化） */
  onToggle?: (open: boolean) => void
}

function kindPrefix(kind: AgentProcessLine['kind']) {
  switch (kind) {
    case 'thought':
      return '推理'
    case 'tool':
      return '工具'
    case 'observation':
      return '观察'
    case 'plan':
      return '计划'
    case 'mode':
      return '模式'
    case 'read':
      return '读取'
    case 'subagent':
      return '子任务'
    default:
      return ''
  }
}

/** 子智能体嵌套卡片：可折叠摘要 + 内部行 + 底部活动态 */
export function AgentSubagentFold({ block }: { block: AgentSubagentBlock }) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const running = block.status === 'running' || block.status === 'pending'
  /** 外部持久化展开态优先（跨页面恢复） */
  const initialOpen = block.externalOpen !== undefined
    ? block.externalOpen
    : (block.forceOpen || !(block.collapsedByDefault ?? true))
  const [open, setOpen] = useState(initialOpen)
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  useEffect(() => {
    if (block.externalOpen !== undefined) setOpen(block.externalOpen)
  }, [block.externalOpen])

  useEffect(() => {
    if (block.forceOpen || running) setOpen(true)
  }, [block.forceOpen, running])

  const body = (
    <div className="border-t border-[color-mix(in_srgb,var(--line)_28%,transparent)] px-2.5 pb-2 pt-1.5">
      {block.summary ? <p className="mb-1.5 text-[12px] leading-5 text-ink-soft">{block.summary}</p> : null}
      {block.lines?.length ? (
        <ul className="mb-1 space-y-0.5 border-l border-[color-mix(in_srgb,var(--line)_36%,transparent)] py-1 pl-2.5">
          {block.lines.map((line) => {
            const prefix = kindPrefix(line.kind)
            return (
              <li key={line.id} className="min-w-0">
                <div className="flex min-w-0 items-start gap-1.5 text-[12px] leading-5 text-ink-soft">
                  <AgentStatusDotSlot status={line.status} size="sm" />
                  <span className="min-w-0 flex-1">
                    {prefix ? <span className="mr-1 text-ink-soft/80">{prefix}</span> : null}
                    <span>{line.label}</span>
                    {line.meta ? (
                      <span className="ml-1.5 text-[11px] text-[color-mix(in_srgb,var(--color-success)_70%,var(--ink-soft))]">
                        {line.meta}
                      </span>
                    ) : null}
                  </span>
                </div>
                {line.detail ? (
                  <p className="mt-0.5 pl-3.5 text-[11px] leading-4 text-ink-soft/85">{line.detail}</p>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      <AgentActivityBar activity={block.activity} running={running} />
    </div>
  )

  return (
    <div
      className={[
        'agent-subagent-fold min-w-0 overflow-hidden',
        angular
          ? 'rounded-none border border-line bg-panel/70'
          : 'rounded-xl border border-[color-mix(in_srgb,var(--line)_50%,var(--accent)_20%)] bg-[color-mix(in_srgb,var(--panel)_90%,var(--surface-muted)_10%)]',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] leading-5 text-ink transition hover:bg-surface-muted/35"
        onClick={() => {
          const next = !open
          setOpen(next)
          block.onToggle?.(next)
        }}
        aria-expanded={open}
      >
        <AgentStatusDotSlot status={block.status ?? (running ? 'running' : 'completed')} size="sm" />
        <span className="shrink-0 text-[11px] font-medium tracking-[0.04em] text-ink-soft">子智能体</span>
        <span className="min-w-0 flex-1 truncate font-medium text-ink">{block.title}</span>
        <motion.svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="shrink-0 text-ink-soft opacity-70"
          animate={{ rotate: open ? 0 : -90 }}
          transition={expandTrans}
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </motion.svg>
      </button>

      {reducedMotion ? (
        open ? body : null
      ) : (
        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              key="subagent-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={expandTrans}
              className="overflow-hidden"
            >
              {body}
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    </div>
  )
}
